/**
 * useSendQueue
 *
 * Owns the four send-queue refs that serialize + dedup rapid user sends in
 * useChat.sendMessage. Extracted (Pass-2 decomposition) via the delegation
 * pattern: this hook is called at the SAME body position where the four
 * `useRef`s used to be declared, and it calls its four `useRef`s unconditionally
 * in the same order — so React's hook order/count is unchanged. The returned ref
 * OBJECTS are the same identity the orchestrator read/wrote before, so every
 * `.current` access in sendMessage / finalizeAssistantMessage / the SSE loop /
 * retryLastMessage / the catch+finally stays byte-identical.
 *
 *   lastSendRef        — 60s identical-content dedup (#1655)
 *   sendInFlightRef    — sub-100ms StrictMode/SSE-reconnect double-invoke guard (#1690)
 *   inFlightTurnRef    — FIFO turn-promise chain; serializes rapid sends (#1696-class)
 *   lastSentContentRef — last sent content for retry (#1696)
 *
 * The control flow (which guard fires when, the await-previous-turn
 * serialization, the resolveTurn bookkeeping) stays inline in sendMessage; this
 * hook supplies the refs + the pure dedup predicate + the window constant so the
 * decision logic has a single source of truth.
 *
 * Pinned by apps/web/src/features/chat/hooks/useChat.send-queue-char.test.ts.
 */
import { useRef } from 'react'

/** Identical-content dedup window: reject a re-send of the same content within 60s (#1655). */
export const CONTENT_DEDUP_WINDOW_MS = 60_000

export interface UseSendQueueReturn {
  /** Last accepted send (content + timestamp) for the 60s identical-content dedup. */
  lastSendRef: React.MutableRefObject<{ content: string; at: number } | null>
  /** Sub-100ms re-entrancy guard against StrictMode / SSE-reconnect double-invokes. */
  sendInFlightRef: React.MutableRefObject<boolean>
  /** Promise resolving when the currently-streaming turn finishes; chains rapid sends FIFO. */
  inFlightTurnRef: React.MutableRefObject<Promise<void> | null>
  /** Last sent content, for retry on transient errors. */
  lastSentContentRef: React.MutableRefObject<string | null>
}

export function useSendQueue(): UseSendQueueReturn {
  // Dedup guard: prevent duplicate sends of the same message
  const lastSendRef = useRef<{ content: string; at: number } | null>(null)
  // Sub-100ms dedup: prevent React StrictMode / SSE reconnect double-invocations (#1690)
  const sendInFlightRef = useRef(false)
  // Promise that resolves when the currently-streaming assistant turn finishes
  // (success, error, or abort). Used to SERIALIZE rapid user sends so the
  // previous assistant response is fully streamed + persisted before the next
  // user message fires, instead of aborting the in-flight stream and losing
  // both the UI render and the server-side persistence.
  const inFlightTurnRef = useRef<Promise<void> | null>(null)
  // Store last sent content for retry (#1696)
  const lastSentContentRef = useRef<string | null>(null)

  return { lastSendRef, sendInFlightRef, inFlightTurnRef, lastSentContentRef }
}

/**
 * Pure predicate for the 60s identical-content dedup. Returns true when a send
 * of `content` should be DROPPED because the same (trimmed) content was sent
 * less than CONTENT_DEDUP_WINDOW_MS ago.
 *
 * Mirrors the inline guard in sendMessage exactly: trims content, compares
 * against the stored trimmed content, and checks the elapsed window.
 */
export function isDuplicateContentSend(
  lastSend: { content: string; at: number } | null,
  content: string,
  now: number,
): boolean {
  return Boolean(
    lastSend &&
      lastSend.content === content.trim() &&
      now - lastSend.at < CONTENT_DEDUP_WINDOW_MS,
  )
}
